import { useCallback, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Box, EmptyState, OrgChart, Skeleton } from "@salestrak/ui";

import { useEntityDetail } from "#app/components/entity";
import { MemberJumpSearch } from "#app/components/org-chart/member-jump-search";
import { OrgChartPersonContent } from "#app/components/org-chart/org-chart-person-content";
import {
  OrgChartViewOptionsMenu,
  orgChartNodeHeight,
  useOrgChartViewOptions,
} from "#app/components/org-chart/org-chart-view-options";
import {
  useTeamOrgChart,
  type OrgChartUserItem,
} from "#app/components/org-chart/use-org-chart-data";

/**
 * `/team/$teamId/chart` — the Team Chart anchor: the workspace org chart's
 * canvas scoped to this team's roster. Reporting lines come from
 * `user.managerId` restricted to in-team pairs (a member whose manager is
 * outside the team roots their own tree); pressing a node opens the profile
 * island, same as the workspace chart.
 */
function TeamChartTabRoute() {
  const { teamId } = Route.useParams();
  const { items, defaultCollapsedIds, ready } = useTeamOrgChart(teamId);
  const { openEntity } = useEntityDetail();

  // "Jump to member" — the ONE find-target concept on this canvas (ring +
  // centering), shared with the workspace chart. The team layout keys this
  // subtree by teamId, so the target resets on team switch.
  const [jumpId, setJumpId] = useState<string | null>(null);

  const viewOptions = useOrgChartViewOptions();
  const { showTitle, showRole } = viewOptions.options;
  // The skeleton mirrors the settled cards, so it stands on the same rung.
  const nodeHeight = orgChartNodeHeight(viewOptions.options);
  const renderNode = useCallback(
    (item: OrgChartUserItem) =>
      // The team slice emits no placeholder nodes — this scope has no
      // "No manager" bucket — so the placeholder arm never renders.
      item.data.kind === "user" ? (
        <OrgChartPersonContent
          showRole={showRole}
          showTitle={showTitle}
          user={item.data.user}
        />
      ) : null,
    [showRole, showTitle],
  );
  const handleNodePress = useCallback(
    (id: string) => openEntity("user", id, { via: "team_chart" }),
    [openEntity],
  );

  return (
    <Box direction="column" fill="flex" minHeight={0}>
      {!ready ? (
        // The charted roster is still assembling — sketch a small indented
        // tree while it arrives, mirroring the workspace chart's skeleton.
        <Box fill="flex" padding="xl">
          <Box direction="column" gap="md">
            <Skeleton height={nodeHeight} width={224} />
            <Box paddingLeft="xl">
              <Skeleton height={nodeHeight} width={224} />
            </Box>
            <Box paddingLeft="xl">
              <Box direction="column" gap="md" paddingLeft="xl">
                <Skeleton height={nodeHeight} width={224} />
                <Skeleton height={nodeHeight} width={224} />
              </Box>
            </Box>
          </Box>
        </Box>
      ) : items.length === 0 ? (
        // Unlike the workspace chart, a team can legitimately be empty.
        <Box fill="flex" align="center" justify="center">
          <EmptyState
            media="icon"
            icon="network"
            title="No members to chart"
            description="Add members to this team to see its reporting lines."
          />
        </Box>
      ) : (
        <OrgChart
          // The find affordance floats over the canvas (top-right panel)
          // rather than costing a toolbar band — the canvas IS the view.
          actions={
            <Box align="center" direction="row" gap="xs">
              <MemberJumpSearch
                items={items}
                onValueChange={setJumpId}
                value={jumpId}
              />
              <OrgChartViewOptionsMenu {...viewOptions} />
            </Box>
          }
          defaultCollapsedIds={defaultCollapsedIds}
          expandable
          // Pressing a node opens the profile island without becoming
          // "active" — only the search target rings.
          focusId={jumpId}
          items={items}
          minimap
          nodeHeight={nodeHeight}
          onNodePress={handleNodePress}
          renderNode={renderNode}
          selectedId={jumpId}
        />
      )}
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/team/$teamId/chart")({
  component: TeamChartTabRoute,
});
